import React, { useState, useEffect } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const h = () => setMob(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return mob;
}

function genCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({length:8}, () => chars[Math.floor(Math.random()*chars.length)]).join('');
}

function BetaAccessBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [config,   setConfig]   = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [newCode,  setNewCode]  = useState('');
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
      const { data: cfg }  = await supabase.from('app_config').select('*').limit(20);
      const { data: wl }   = await supabase.from('waitlist').select('email,name,created_at').order('created_at',{ascending:false}).limit(50);
      setConfig(cfg || []);
      setWaitlist(wl || []);
      } catch (_e) {} finally { setLoading(false); }
    })();
  }, []);

  async function saveCode() {
    if (!newCode.trim()) return;
    setSaving(true);
    try {
    const { error } = await supabase.from('app_config').upsert({ key: 'beta_code', value: newCode.trim() }, { onConflict: 'key' });
    if (!error) {
      setConfig(prev => { const next = prev.filter(r=>r.key!=='beta_code'); return [...next, {key:'beta_code',value:newCode.trim()}]; });
      setMsg('Beta code updated. Note: the app currently reads this from env var REACT_APP_BETA_CODE — updating the DB sets the new code for any future app build that reads from app_config.');
      setNewCode('');
    }
    } catch (_e) {} finally { setSaving(false); }
  }

  const currentCode = config?.find(r=>r.key==='beta_code')?.value || '2314 (env var default)';

  return (
    <div style={{ fontFamily:F, padding: isMobile ? '16px 14px 80px' : '24px 28px' }}>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:'1.1rem', fontWeight:500, margin:0, color:'var(--color-text-primary)' }}>Beta Access</h2>
        <p style={{ fontSize:'.78rem', color:'var(--color-text-secondary)', margin:'4px 0 0' }}>
          Manage the beta access code and waitlist.
        </p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:16 }}>

        {/* Current code */}
        <Card>
          <CardTitle>Access code</CardTitle>
          <div style={{ background:'#0d1d2b', borderRadius:8, padding:'12px 16px', marginBottom:14, fontFamily:'monospace', fontSize:'1.4rem', letterSpacing:'.15em', color:'#AFE1F6', textAlign:'center' }}>
            {loading ? '…' : currentCode}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <input value={newCode} onChange={e=>setNewCode(e.target.value.toUpperCase())} placeholder="New code (e.g. SANTEST1)"
              style={{ flex:1, padding:'7px 12px', borderRadius:8, border:'1px solid var(--color-border-secondary)', background:'var(--color-background-secondary)', color:'var(--color-text-primary)', fontSize:'.78rem', fontFamily:'monospace', letterSpacing:'.08em' }} />
            <button onClick={()=>setNewCode(genCode())}
              style={{ padding:'7px 10px', borderRadius:8, border:'1px solid var(--color-border-secondary)', background:'none', color:'var(--color-text-secondary)', fontSize:'.72rem', cursor:'pointer', fontFamily:F }}>
              Gen
            </button>
            <button onClick={saveCode} disabled={saving||!newCode.trim()}
              style={{ padding:'7px 14px', borderRadius:8, background:t.accent, color:'#AFE1F6', border:'none', fontSize:'.72rem', fontWeight:500, cursor:'pointer', fontFamily:F }}>
              {saving?'Saving…':'Save'}
            </button>
          </div>
          {msg && <Insight type="info" style={{marginTop:10}}>{msg}</Insight>}
          <p style={{ fontSize:'.68rem', color:'var(--color-text-secondary)', marginTop:10 }}>
            Beta URL: <a href="https://www.sanoire.com" target="_blank" rel="noreferrer" style={{color:t.mut}}>sanoire.com</a>
          </p>
        </Card>

        {/* Waitlist stats */}
        <Card>
          <CardTitle>Waitlist ({waitlist.length})</CardTitle>
          {loading ? <div style={{color:'var(--color-text-secondary)',fontSize:'.78rem'}}>Loading…</div> :
           waitlist.length === 0 ? <Insight type="info">No waitlist entries yet.</Insight> :
          <div style={{ overflowY:'auto', maxHeight:240 }}>
            {waitlist.map((w,i) => (
              <div key={i} style={{ padding:'5px 0', borderBottom:'.5px solid var(--color-border-tertiary)', fontSize:'.72rem' }}>
                <span style={{color:'var(--color-text-primary)',fontWeight:500}}>{w.email}</span>
                {w.name && <span style={{color:'var(--color-text-secondary)',marginLeft:6}}>{w.name}</span>}
                <span style={{color:'var(--color-text-secondary)',float:'right'}}>{w.created_at?.slice(0,10)}</span>
              </div>
            ))}
          </div>}
        </Card>
      </div>
    </div>
  );
}

export default function BetaAccess(props) {
  if (props && props.embed) return <BetaAccessBody {...props} />;
  return <ConsoleShell title="Beta Access"><BetaAccessBody {...props} /></ConsoleShell>;
}
